import { Component, instantiate, Node, Sprite, TransformBit, Vec3, _decorator } from 'cc';
import { DEV } from 'cc/env';
import Game from '../../../Game';
import NodePoolInstance from '../../CCClass/NodePoolInstance';
import Delay from '../../Utils/Delay';

enum EventType {
    DRAW_AREA_CHANGED = 'DRAW_AREA_CHANGED',
}

const { ccclass, property } = _decorator;
const tempCurrentPosition: Vec3 = new Vec3(0, 0, 0);
const tempNextPosition: Vec3 = new Vec3(0, 0, 0);
const tempDirection: Vec3 = new Vec3(0, 0, 0);
const tempMovement: Vec3 = new Vec3(0, 0, 0);

@ccclass('ScratchBlush')
export default class ScratchBlush extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property(Sprite)
    private readonly sprite: Sprite = null;

    @property({
        displayName: DEV && '額外筆刷預生成數量',
    })
    private readonly prespawnCount: number = 50;

    @property({
        displayName: DEV && '筆刷間隔',
        tooltip: DEV && '筆刷瞬間移動距離過長時會在移動路徑上添加額外的筆刷，間隔越小會嘗試添加越多的筆刷在路徑上',
        min: 0,
        step: 1,
    })
    private readonly blushSpacing: number = 5;

    @property({
        displayName: DEV && '刮除音效',
    })
    private readonly scratchSoundEffect: string = '';

    private _pool: NodePoolInstance = null;
    private _initialized: boolean = false;
    private _hasLastPosition: boolean = false;
    private _lastPosition: Vec3 = new Vec3();
    private _isPlayingSoundEffect: boolean = false;

    public get drawing(): boolean {
        return this.node.activeInHierarchy;
    }

    public get lastPosition(): Vec3 {
        return this._lastPosition;
    }

    public onLoad() {
        this.on(Node.EventType.TRANSFORM_CHANGED, this._onTransformChanged, this);
        this.sprite.on(Node.EventType.SIZE_CHANGED, this._notifyDrawAreaChanged, this);
        this.init();
    }

    public onEnable() {
        this._hasLastPosition = false;
        this._toggleDisplay(false);
    }

    public onDisable() {
        this._stopSoundEffect();
        this.unschedule(this._stopSoundEffect);
    }

    public init(): void {
        if (this._initialized) {
            return;
        }
        this._initialized = true;

        // setup pool
        this._pool = new NodePoolInstance();
        this._pool.root = this.node.parent;
        this._pool.clonedNode = instantiate(this.sprite.node);
        this._pool.init((node: Node) => {
            // ensure addtional blush can be displayed correctly
            node.active = true;
            node.getComponent(Sprite).enabled = true;
        });
        if (this.prespawnCount) {
            this._pool.prespawn(this.prespawnCount);
        }
    }

    public setLayer(layer: number): void {
        this.sprite.node.layer = layer;
    }

    public draw(drawing: boolean, drawImmediately: boolean = false): void {
        this.node.active = drawing;
        if (drawing && drawImmediately) {
            this._drawAtCurrent();
        }
    }

    private _toggleDisplay(display: boolean): void {
        this.sprite.enabled = display;
    }

    private _onTransformChanged(transformBit: TransformBit) {
        if ((transformBit as number) & TransformBit.POSITION) {
            this._drawAtCurrent();
        }
        this._notifyDrawAreaChanged();
    }

    private _notifyDrawAreaChanged() {
        this.emit(ScratchBlush.EVENT_TYPE.DRAW_AREA_CHANGED);
    }

    private _drawAtCurrent(): void {
        if (!this.drawing) {
            return;
        }
        // displaying the blush sprite after position changed while the sprite is active
        this._toggleDisplay(true);

        if (!this._hasLastPosition) {
            this._hasLastPosition = true;
        } else {
            // add addtional blush in the path to the target position if the distance is too long to avoid a gap
            this.node.getWorldPosition(tempNextPosition);
            tempCurrentPosition.set(this._lastPosition);
            const distance = Vec3.distance(tempNextPosition, this._lastPosition);
            const maxSpacing = this.blushSpacing;
            if (distance > maxSpacing) {
                tempDirection.set(tempNextPosition).subtract(tempCurrentPosition).normalize();
                const addtionalBlushs: Node[] = [];
                let distanceLeft = distance;
                while (distanceLeft > maxSpacing) {
                    const addtionalBlush = this._pool.get();
                    // sync layer
                    addtionalBlush.layer = this.sprite.node.layer;
                    // calculate the position
                    tempMovement.set(tempDirection).multiplyScalar(maxSpacing);
                    tempCurrentPosition.add(tempMovement);
                    addtionalBlush.setWorldPosition(tempCurrentPosition);
                    // store blush to recycle next frame and decrease the distance left
                    addtionalBlushs.push(addtionalBlush);
                    distanceLeft -= maxSpacing;
                }
                Delay.resolve(0).then(() => {
                    addtionalBlushs.forEach((addtionalBlush) => this._pool.put(addtionalBlush));
                });
            }
        }

        // store the last position of the blush sprite
        this.node.getWorldPosition(this._lastPosition);

        this._playSoundEffect();
    }

    private _playSoundEffect(): void {
        if (!this.scratchSoundEffect) {
            return;
        }
        // stop playing sound effect after 0.2 seconds if no new playSoundEffect called (i.e. no new scratch action)
        this.unschedule(this._stopSoundEffect);
        this.scheduleOnce(this._stopSoundEffect, 0.2);
        if (this._isPlayingSoundEffect) {
            return;
        }
        this._isPlayingSoundEffect = true;
        Game.audio.playEffect(this.scratchSoundEffect, true, 0, 1, true);
    }

    private _stopSoundEffect(): void {
        if (!this._isPlayingSoundEffect) {
            return;
        }
        this._isPlayingSoundEffect = false;
        Game.audio.stopEffect(this.scratchSoundEffect);
    }
}
